import type { ViewingHistoryItemResponse } from '../types/library.types';
import type { Video } from '../types/video.types';
import type { VideoCardData } from '../types/video-card.types';
import { buildVideoCardData } from './video-card';
import { Routes } from '../types/constants';

export const CONTINUE_WATCHING_RESTART_THRESHOLD_PERCENT = 95;

/**
 * Options for building resumable viewing-history cards.
 */
export interface BuildContinueWatchingOptions {
    /** Maximum number of cards to return. */
    limit?: number;
    /** Video URLs to omit from the result set. */
    excludeVideoUrls?: string[];
    /** Video IDs to omit from the result set. */
    excludeVideoIds?: string[];
    /** Include completed videos instead of limiting to unfinished items. */
    includeCompleted?: boolean;
    /** Progress percentage at which a video resumes from the beginning. */
    restartThresholdPercent?: number;
}

/**
 * UI-ready viewing-history item for continue-watching surfaces.
 */
export interface ContinueWatchingItem {
    /** Stable key for rendering. */
    id: string;
    /** Source video data. */
    video: Video;
    /** VideoCard-compatible data with a resume URL. */
    card: VideoCardData;
    /** Integer progress percentage from 0 to 100. */
    progressPercent: number;
    /** Resume position in seconds. */
    resumeSeconds: number;
    /** Formatted resume timestamp. */
    resumeLabel: string;
    /** Formatted last watched date. */
    lastWatchedLabel: string;
}

const normalizeKey = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const clampProgress = (value: number) => Math.min(100, Math.max(0, Math.round(value)));

/**
 * Format seconds as a compact timestamp.
 */
export const formatResumeTime = (value?: number | null) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return '0:00';
    }

    const rounded = Math.max(0, Math.floor(value));
    const hours = Math.floor(rounded / 3600);
    const minutes = Math.floor((rounded % 3600) / 60);
    const seconds = rounded % 60;

    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format the last-watched timestamp for display.
 */
export const formatLastWatchedDate = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric'
    }).format(parsed);
};

/**
 * Build the watch URL that resumes a video from a safe timestamp.
 */
export const buildResumeWatchUrl = (video: Video, resumeSeconds: number) => {
    const params = new URLSearchParams();
    params.set('v', video.Url);
    params.set('t', String(Math.max(0, Math.floor(resumeSeconds))));
    return `${Routes.WATCH}?${params.toString()}`;
};

/**
 * Convert viewing-history response rows into continue-watching cards.
 */
export const buildContinueWatchingItems = (
    items: ViewingHistoryItemResponse[],
    {
        limit = 6,
        excludeVideoUrls = [],
        excludeVideoIds = [],
        includeCompleted = false,
        restartThresholdPercent = CONTINUE_WATCHING_RESTART_THRESHOLD_PERCENT
    }: BuildContinueWatchingOptions = {}
): ContinueWatchingItem[] => {
    const excludedUrls = new Set(excludeVideoUrls.map(normalizeKey));
    const excludedIds = new Set(excludeVideoIds.map(normalizeKey));

    return [...items]
        .sort((left, right) => {
            const leftTime = Date.parse(left.LastWatchedAtUtc) || 0;
            const rightTime = Date.parse(right.LastWatchedAtUtc) || 0;
            return rightTime - leftTime;
        })
        .filter((item) => {
            const video = item.Video;
            if (!video?.Url || excludedUrls.has(normalizeKey(video.Url)) || excludedIds.has(normalizeKey(video.Id))) {
                return false;
            }

            const progressPercent = clampProgress(item.ProgressPercent ?? 0);
            const isCompleted = item.IsCompleted || progressPercent >= restartThresholdPercent;
            return includeCompleted || !isCompleted;
        })
        .slice(0, limit)
        .map((item) => {
            const progressPercent = clampProgress(item.ProgressPercent ?? 0);
            const resumeSeconds = progressPercent >= restartThresholdPercent
                ? 0
                : Math.max(0, Math.floor(item.ResumeSeconds ?? 0));
            const card = buildVideoCardData(item.Video);
            card.watchUrl = buildResumeWatchUrl(item.Video, resumeSeconds);

            return {
                id: `${item.Video.Id || item.Video.Url}-${item.LastWatchedAtUtc}`,
                video: item.Video,
                card,
                progressPercent,
                resumeSeconds,
                resumeLabel: formatResumeTime(resumeSeconds),
                lastWatchedLabel: formatLastWatchedDate(item.LastWatchedAtUtc)
            };
        });
};
